import CaseStudy from '../components/CaseStudy';

const data = {
  company: 'Personal Project · 2025',
  title: 'The Brief:<br/>AI-Powered Case<br/>Study Writer',
  subtitle: 'Built my own tool that converts rough designer notes into portfolio case studies — 200 users in the first week, case study writing time down from days to under an hour.',
  metaRole: 'Founder + Solo Designer/Developer',
  metaPlatform: 'Web App (SaaS)',
  metaExtraLabel: 'Type',
  metaExtra: '0 → 1 · Product Thinking',
  outcomeValue: '200 users',
  outcomeSub: 'in the first week of launch',
  heroPlaceholder: 'Hero image: Full-width screenshot of The Brief web app - showing the input editor and generated case study output side by side',
  heroImage: '/brief-hero.png',
  metaItems: [
    { label: 'My Role', value: 'Founder · Designer · Developer' },
    { label: 'Platform', value: 'Web App (SaaS)' },
    { label: 'Type', value: '0 → 1 · Product Thinking' },
    { label: 'Outcome', value: '200 users · Week 1' },
  ],
  sections: [
    {
      type: 'text',
      label: 'The Problem I Had',
      heading: 'Designers do the work. Then they delay writing about it for months.',
      paragraphs: [
        'Every designer knows the feeling: you shipped something meaningful, but turning it into a portfolio case study is a completely different kind of work. The project lives in your head, across Figma comments, Slack threads, and half-finished Notion docs. Getting from "I did this" to something a recruiter can read takes days - and often just doesn\'t happen.',
        'I was in the same position. I had the work. I had the outcomes. What I was missing was the structure to turn scattered notes into a coherent narrative. So I built the tool I wished existed.',
      ],
    },
    {
      type: 'text',
      label: 'Insight',
      heading: 'The bottleneck isn\'t writing. It\'s the blank page.',
      paragraphs: [
        'Talking to designers early on, the pattern was clear: nobody struggled to <em>explain</em> their work - they could talk through a project fluently in a 10-minute conversation. The struggle was sitting down to write it cold, without structure, not knowing where to start.',
        '<strong>The blank page was the real problem.</strong> Once someone had a strong first draft - even an imperfect one - they could edit it into something great quickly. The tool just needed to eliminate the blank page entirely.',
      ],
      hmw: 'How might we get designers from rough project notes to a structured, shareable case study - without making the input feel like more work than the writing?',
    },
    {
      type: 'text',
      label: 'Solution',
      heading: 'Paste your notes. Get a case study.',
      paragraphs: [
        'The Brief is a lightweight web app where designers paste raw project notes - context, decisions, outcomes, whatever they have - and the AI structures and writes the case study, interview prep answers, and a recruiter-ready summary.',
        '<strong>Three output formats:</strong> a full portfolio case study, a structured set of STAR-format interview answers, and a one-paragraph recruiter summary. <strong>Editable outputs</strong> - the AI gives a strong draft, you refine from there. <strong>Minimal input required</strong> - designed to work with messy, incomplete notes, not polished briefs.',
      ],
    },
    {
      type: 'image',
      size: 'tall',
      src: '',
      text: 'Screenshot: The Brief input screen - showing the project notes editor with guiding prompts and the generate button',
      src: '/brief-levers.png',
      caption: 'Input screen - a single freeform field, no structured forms to fill in',
    },
    {
      type: 'image',
      size: 'medium',
      src: '',
      text: 'Screenshot: Generated case study output - structured sections with editable inline text',
      src: '/brief-hero.png',
      caption: 'Generated case study output with inline editing',
    },
    {
      type: 'image',
      size: 'medium',
      src: '',
      text: 'Screenshot: Interview prep output - STAR-format answers generated from the same project notes',
      src: '/brief-interview.png',
      caption: 'Interview prep mode - same notes, recruiter-ready format',
    },
    {
      type: 'text',
      label: 'Design Decisions',
      heading: 'What I deliberately left out.',
      paragraphs: [
        'Early versions had structured input fields - project type, timeline, team size, outcomes. They got cut. <strong>The more the input felt like a form, the more it felt like homework.</strong> Designers would stall trying to fill fields "correctly" instead of just dumping what they knew.',
        'The final input is a single freeform textarea with a few loose guiding prompts. That decision alone made the tool feel approachable rather than effortful - and it showed in how quickly people started using it after landing on the page.',
      ],
    },
    {
      type: 'text',
      label: 'Impact',
      heading: 'From days of delay to done in an hour.',
      paragraphs: [
        'One user told me she had been putting off her portfolio for six months. Within two hours of using The Brief, all her case studies were written. That was the moment I knew the product had found something real.',
      ],
      impact: [
        { num: '200', label: 'active users in the first week of launch' },
        { num: 'Days → 1hr', label: 'reduction in case study writing time, reported by users' },
        { num: '100%', label: 'satisfaction rate on generated case studies' },
      ],
    },
    {
      type: 'text',
      label: 'Learnings',
      heading: 'The best validation is a problem you\'ve lived yourself.',
      paragraphs: [
        'When you\'re the designer, developer, and the target user, the feedback loop is immediate - but so is the temptation to over-build. The hardest discipline was <strong>shipping the smallest version that was genuinely useful</strong>, not the version I had in my head.',
        'The 200-user first week wasn\'t from marketing. It was from sharing honestly in designer communities - "I built this because I needed it." That specificity resonated. People knew immediately whether the problem was theirs too.',
      ],
    },
  ],
  nextTo: '/propel',
  nextLabel: 'Next Project',
  nextTitle: 'From Job Search to Networking Platform',
};

export default function TheBrief() {
  return <CaseStudy data={data} />;
}
